import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { getDashboardStats } from '../../services/api';
import {
  Package, FileText, Truck, CalendarCheck, ArrowRight,
  CheckCircle, ClockCounterClockwise, WarningCircle, Anchor, ArrowsClockwise
} from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const ORDER_STATUS_LABELS = {
  new_order: 'New Order', order_submitted: 'Submitted', ready_to_load: 'Ready to Load',
  loaded: 'Loaded', shipped: 'Shipped', arrived_alg: 'Arrived ALG',
  cleared_port: 'Cleared Port', delivered: 'Delivered', cancelled: 'Cancelled',
  draft: 'Draft', active: 'Active', closed: 'Closed',
  payment_received: 'Payment Received', bl_issued: 'BL Issued',
};

const STATUS_CARD_CONFIG = [
  { key: 'new_orders', status: 'new_order', label: 'New Orders', icon: FileText, color: 'bg-blue-600' },
  { key: 'order_submitted', status: 'order_submitted', label: 'Submitted', icon: ClockCounterClockwise, color: 'bg-indigo-600' },
  { key: 'ready_to_load', status: 'ready_to_load', label: 'Ready to Load', icon: WarningCircle, color: 'bg-amber-500' },
  { key: 'loaded', status: 'loaded', label: 'Loaded', icon: Package, color: 'bg-purple-600' },
  { key: 'shipped', status: 'shipped', label: 'Shipped', icon: Truck, color: 'bg-violet-600' },
  { key: 'arrived_alg', status: 'arrived_alg', label: 'Arrived ALG', icon: Anchor, color: 'bg-teal-600' },
  { key: 'cleared_port', status: 'cleared_port', label: 'Cleared Port', icon: ArrowsClockwise, color: 'bg-cyan-600' },
  { key: 'delivered', status: 'delivered', label: 'Delivered', icon: CheckCircle, color: 'bg-emerald-600' },
];

const DashboardPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadStats = useCallback(async () => {
    try {
      const res = await getDashboardStats();
      setStats(res.data);
    } catch {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  const chartData = stats?.orders_by_status
    ? Object.entries(stats.orders_by_status).map(([status, count]) => ({
        name: ORDER_STATUS_LABELS[status] || status,
        status,
        count
      }))
    : [];

  const handleStatusClick = (status) => {
    navigate(`/orders?status=${status}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="mb-4">
        <h1 className="text-3xl font-bold tracking-tight">{t('dashboard.title')}</h1>
        <p className="text-muted-foreground mt-1">{t('dashboard.subtitle')}</p>
      </div>

      {/* Status Cards Grid — All clickable */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="status-cards">
        {STATUS_CARD_CONFIG.map(({ key, status, label, icon: Icon, color }) => {
          const count = stats?.[key] || 0;
          return (
            <div
              key={key}
              onClick={() => handleStatusClick(status)}
              className="p-4 border-2 border-border bg-white cursor-pointer hover:border-primary/50 hover:shadow-md transition-all group"
              data-testid={`stat-${status}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className={`w-9 h-9 ${color} flex items-center justify-center rounded`}>
                  <Icon weight="duotone" className="w-5 h-5 text-white" />
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-0.5">{label}</p>
              <p className="text-2xl font-bold tracking-tight">{count}</p>
            </div>
          );
        })}
      </div>

      {/* Charts and Recent Orders */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Orders by Status Chart */}
        <div className="lg:col-span-2 card-brutalist">
          <h3 className="text-lg font-bold mb-4">Orders by Status</h3>
          <div className="h-72" style={{ minWidth: 0, minHeight: 100 }}>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="99%" height="100%">
                <BarChart data={chartData} layout="vertical" margin={{ left: 20, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#002DCC" cursor="pointer"
                    onClick={(data) => { if (data?.status) handleStatusClick(data.status); }} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground">
                {t('dashboard.noOrders')}
              </div>
            )}
          </div>
        </div>

        {/* Recent Orders */}
        <div className="card-brutalist">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold">{t('dashboard.recentOrders')}</h3>
            <Link to="/orders" className="text-sm text-primary hover:underline inline-flex items-center">
              {t('dashboard.viewAll')} <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </div>

          {stats?.recent_orders?.length > 0 ? (
            <div className="space-y-3">
              {stats.recent_orders.map((order, idx) => (
                <Link
                  key={order.id}
                  to="/orders"
                  className={`block p-3 border border-border hover:bg-secondary/50 transition-colors animate-fade-in stagger-${idx + 1}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-mono text-sm font-medium">{order.order_number}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
                      order.status === 'delivered' ? 'bg-green-100 text-green-700' :
                      order.status === 'shipped' ? 'bg-violet-100 text-violet-700' :
                      order.status === 'new_order' ? 'bg-blue-100 text-blue-700' :
                      order.status === 'order_submitted' ? 'bg-indigo-100 text-indigo-700' :
                      order.status === 'arrived_alg' ? 'bg-teal-100 text-teal-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {ORDER_STATUS_LABELS[order.status] || order.status}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {order.client_name || 'Unknown'} {order.client_type === 'dealer' ? `(${t('dashboard.dealer')})` : ''}
                  </p>
                  {order.car_info && (
                    <p className="text-xs font-medium mt-0.5">{order.car_info} ({order.cars_count} {order.cars_count === 1 ? 'car' : 'cars'})</p>
                  )}
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              {t('dashboard.noRecentOrders')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
